import { useId, useState } from 'react'
import { toast } from 'sonner'
import IconPicker from './IconPicker'
import { EMPTY_TAG } from './tagUtils'
import { FONT_STYLE_OPTIONS, POSITION_OPTIONS } from './tagFormOptions'
import { TagChipContent } from './post-creator/tagChipContent'
import {
  Button,
  Dialog,
  ErrorSummary,
  Field,
  FormLayout,
  FormLayoutRow,
  Input,
  Select,
} from './common'
import styles from './TagForm.module.css'

// A colour as hex text, with a native picker inside the field editing the same value.
function ColorField({ label, pickerLabel, value, onChange }) {
  return (
    <Field label={label}>
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={styles.colorText}
        actions={[
          <Input
            key="picker"
            type="color"
            aria-label={pickerLabel}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={styles.colorPicker}
          />,
        ]}
      />
    </Field>
  )
}

/**
 * Reusable tag create/edit modal.
 *
 * Props:
 * - initialData: tag field values (uses EMPTY_TAG defaults if omitted)
 * - title: modal heading, e.g. "Create Tag" / "Edit Tag"
 * - saveLabel: text for the save button, e.g. "Create" / "Save"
 * - onSave(formData): async — parent does the API call, throws on error
 * - onClose(): dismiss the modal
 */
function TagForm({ initialData, title = 'Create Tag', saveLabel = 'Create', onSave, onClose }) {
  const [formData, setFormData] = useState({ ...EMPTY_TAG, ...initialData })
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const iconSearchId = useId()

  const handleSave = async () => {
    if (!formData.label.trim()) return
    setSaving(true)
    setSaveError('')
    try {
      await onSave({ ...formData, icon: formData.icon || undefined })
      onClose()
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to save tag'
      setSaveError(message)
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }


  return (
    <Dialog
      open
      title={title}
      description="Set the tag label and how it appears on feed content."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} loading={saving} disabled={!formData.label.trim()}>
            {saveLabel}
          </Button>
        </>
      }
    >
      <FormLayout
        className={styles.form}
        errorSummary={saveError ? <ErrorSummary title="Tag could not be saved">{saveError}</ErrorSummary> : null}
      >
        <Field label="Label" required>
          <Input
            value={formData.label}
            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
            placeholder="e.g. LIVE, BOOSTED"
            data-autofocus
          />
        </Field>

        <FormLayoutRow columns={2}>
          <ColorField
            label="Background color"
            pickerLabel="Choose background color"
            value={formData.bg_color}
            onChange={(bg_color) => setFormData({ ...formData, bg_color })}
          />
          <ColorField
            label="Text color"
            pickerLabel="Choose text color"
            value={formData.text_color}
            onChange={(text_color) => setFormData({ ...formData, text_color })}
          />
        </FormLayoutRow>

        <FormLayoutRow columns={2}>
          <div className={styles.iconField}>
            <label htmlFor={iconSearchId}>Icon</label>
            <IconPicker
              value={formData.icon}
              onChange={(icon) => setFormData({ ...formData, icon })}
              searchInputId={iconSearchId}
              searchLabel="Search tag icons"
            />
          </div>
          <Field label="Font style">
            <Select
              value={formData.font_style}
              onChange={(e) => setFormData({ ...formData, font_style: e.target.value })}
            >
              {FONT_STYLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </Field>
        </FormLayoutRow>

        <FormLayoutRow columns={2}>
          <Field label="Position">
            <Select
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            >
              {POSITION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Sort order">
            <Input
              type="number"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
            />
          </Field>
        </FormLayoutRow>

        <div className={styles.preview} aria-label="Tag preview">
          <span>Preview</span>
          <TagChipContent tag={formData} fallbackLabel="TAG" />
        </div>
      </FormLayout>
    </Dialog>
  )
}

export default TagForm
